'use client';

import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { getFirebaseAuth } from '@/lib/firebase/client';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [supabase] = useState(() => createClient());

  useEffect(() => {
    const firebaseAuth = getFirebaseAuth();
    if (!firebaseAuth) return;
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      setUser(firebaseUser);
      setSession(firebaseUser ? { user: firebaseUser } : null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const getAuth = () => {
    const firebaseAuth = getFirebaseAuth();
    if (!firebaseAuth) throw new Error('Firebase auth not available');
    return firebaseAuth;
  };

  // Email/Password Sign Up
  const signUp = async (
    email: string,
    password: string,
    metadata: { fullName?: string; avatarUrl?: string } = {}
  ) => {
    const firebaseAuth = getAuth();
    const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
    
    if (metadata.fullName || metadata.avatarUrl) {
      await updateProfile(userCredential.user, {
        displayName: metadata.fullName,
        photoURL: metadata.avatarUrl,
      });
      setUser(userCredential.user);
    }

    return { user: userCredential.user };
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    const firebaseAuth = getAuth();
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(firebaseAuth, provider);
    return { user: userCredential.user };
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const firebaseAuth = getAuth();
    const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
    return { user: userCredential.user };
  };

  // Sign Out
  const signOut = async () => {
    const firebaseAuth = getAuth();
    await firebaseSignOut(firebaseAuth);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const firebaseAuth = getAuth();
    return firebaseAuth.currentUser;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.emailVerified ?? false;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.uid)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    
    return {
      id: user.uid,
      full_name: user.displayName,
      avatar_url: user.photoURL,
      email: user.email,
      ...data
    };
  };

  // Request Password Reset
  const resetPassword = async (email: string) => {
    const firebaseAuth = getAuth();
    await sendPasswordResetEmail(firebaseAuth, email);
  };

  // Update Password
  const updatePassword = async (newPassword: string) => {
    const firebaseAuth = getAuth();
    if (firebaseAuth.currentUser) {
      await firebaseUpdatePassword(firebaseAuth.currentUser, newPassword);
    } else {
      throw new Error("No user is currently signed in.");
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:156:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="156" data-component-end-line="156" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
